import React, {
  useState,
  useCallback,
  useRef,
  useEffect,
  useMemo,
} from "react";
import { useDropzone } from "react-dropzone";
import { useMsal } from "@azure/msal-react";
import { CircularProgress, Snackbar, Alert, Pagination } from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import Fuse from "fuse.js";
import {
  ArrowUpTrayIcon,
  DocumentIcon,
  XCircleIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  DocumentTextIcon,
  DocumentChartBarIcon,
  PaperClipIcon,
  ArrowPathIcon,
  TrashIcon,
  CloudArrowDownIcon,
  MagnifyingGlassIcon,
} from "@heroicons/react/24/outline";
import { Breadcrumb } from "../../components/Breadcrumb";
import httpClient, { TimeoutError } from "../../utils/httpClient";
import {
  useFileAttachmentsByQuarter,
  useDownloadFileAttachment,
  useDeleteFileAttachment,
  bonusKeys,
  useFileAttachmentsByEmail,
  useFileAttachmentsByEmployee,
} from "../../hooks/useBonusQueries";
import { FileAttachment } from "../../services/bonus/types";

interface UploadedFile {
  file: File;
  preview: string;
  status: "pending" | "uploading" | "success" | "error" | "retrying";
  message?: string;
  retryCount?: number;
  id?: string; // For tracking uniqueness in retries
}

// Define a type for the upload response
interface UploadResponse {
  success: boolean;
  fileId?: number;
  message?: string;
  // Add any other properties that your API returns
}

// File upload mutation function
const uploadFile = async ({
  file,
  roleType,
  retryCount = 0,
  quarter = null,
}: {
  file: File;
  roleType: string;
  retryCount?: number;
  quarter?: string | null;
}): Promise<UploadResponse> => {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("request", "");

  // Add quarter data if provided (for Ops users)
  if (quarter) {
    formData.append("quarter", quarter);
  }

  // Give up after 3 retries
  if (retryCount > 3) {
    throw new Error("Maximum retry attempts reached");
  }

  try {
    const response = await httpClient.post("/api/upload-file", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
      timeout: 30000, // 30 seconds timeout
    });

    return response.data;
  } catch (error: any) {
    // Check for network errors that might benefit from a retry
    if (
      error.code === "ECONNABORTED" ||
      error.message?.includes("timeout") ||
      !error.response ||
      error.response?.status >= 500
    ) {
      console.log(`Attempt ${retryCount + 1} failed, retrying...`);
      // Add exponential backoff delay
      await new Promise((resolve) =>
        setTimeout(resolve, 1000 * Math.pow(2, retryCount))
      );
      return uploadFile({
        file,
        roleType,
        retryCount: retryCount + 1,
        quarter,
      });
    }

    throw error;
  }
};

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return bytes + " B";
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
  return (bytes / (1024 * 1024)).toFixed(1) + " MB";
};

const Upload: React.FC = () => {
  const { accounts } = useMsal();
  const [selectedRole] = useState<string>(
    localStorage.getItem("selectedRole") || "CCM1"
  );
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const [previewFile, setPreviewFile] = useState<UploadedFile | null>(null);

  // New state for current quarter and user email
  const [currentQuarter, setCurrentQuarter] = useState<string>("2025Q1"); // This should be dynamic in a real app
  const [userEmail, setUserEmail] = useState<string>("");
  const [viewType, setViewType] = useState<"quarter" | "employee" | "email">(
    "employee"
  );
  const [employeeId, setEmployeeId] = useState<number | null>(null);

  // New state for search, pagination, and role checking
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [itemsPerPage] = useState<number>(10);
  const [userRole, setUserRole] = useState<string>("");
  const isOpsRole = userRole === "Ops";

  // New state for multi-select functionality
  const [selectedFiles, setSelectedFiles] = useState<Set<number>>(new Set());
  const [isSelectMode, setIsSelectMode] = useState<boolean>(false);

  const [alert, setAlert] = useState<{
    show: boolean;
    message: string;
    type: "success" | "error" | "info";
  }>({
    show: false,
    message: "",
    type: "info",
  });
  const fileCountRef = useRef(0);
  const queryClient = useQueryClient();

  // Get documents based on selected view type
  const quarterAttachmentsQuery = useFileAttachmentsByQuarter(currentQuarter);
  const emailAttachmentsQuery = useFileAttachmentsByEmail(userEmail);
  const employeeAttachmentsQuery = useFileAttachmentsByEmployee(
    employeeId || 0
  );

  // Determine which query to use based on view type
  const fileAttachmentsQuery =
    viewType === "quarter"
      ? quarterAttachmentsQuery
      : viewType === "email"
      ? emailAttachmentsQuery
      : employeeAttachmentsQuery;

  // Initialize download and delete mutations
  const downloadFileMutation = useDownloadFileAttachment();
  const deleteFileMutation = useDeleteFileAttachment();

  // Set user email from MSAL account
  useEffect(() => {
    if (accounts && accounts.length > 0) {
      setUserEmail(accounts[0].username);
      // For demo purposes, set a sample employee ID (in a real app, this would come from the user profile)
      setEmployeeId(0);

      // Get user role from localStorage
      const role = localStorage.getItem("selectedRole") || "";
      setUserRole(role);

      // If user is not Ops, default to employee view
      if (role !== "Ops" && viewType === "quarter") {
        setViewType("employee");
      }
    }
  }, [accounts, viewType]);

  // Log the documents when the query completes
  useEffect(() => {
    if (fileAttachmentsQuery.data) {
      console.log("File documents:", fileAttachmentsQuery.data);
    }
  }, [fileAttachmentsQuery.data]);

  // Search and filter documents
  const filteredAttachments = useMemo(() => {
    if (!fileAttachmentsQuery.data) return [];

    // If no search query, return all data
    if (!searchQuery) return fileAttachmentsQuery.data;

    // Setup Fuse.js for fuzzy searching
    const fuseOptions = {
      keys: ["fileName", "quarter", "employeeName", "email"],
      threshold: 0.4, // Lower threshold = more strict matching
    };

    const fuse = new Fuse(fileAttachmentsQuery.data, fuseOptions);
    return fuse.search(searchQuery).map((result) => result.item);
  }, [fileAttachmentsQuery.data, searchQuery]);

  // Pagination
  const totalPages = Math.ceil(
    (filteredAttachments?.length || 0) / itemsPerPage
  );
  const paginatedAttachments = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredAttachments.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredAttachments, currentPage, itemsPerPage]);

  // Handle page change
  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    page: number
  ) => {
    setCurrentPage(page);
  };

  // Create upload mutation
  const uploadMutation = useMutation({
    mutationFn: uploadFile,
    onError: (error) => {
      console.error("Upload error:", error);
      setAlert({
        show: true,
        message: "One or more files failed to upload. Please try again.",
        type: "error",
      });
    },
    onSuccess: () => {
      // Check if all files are done
      const allDone = uploadedFiles.every(
        (f) => f.status === "success" || f.status === "error"
      );

      if (allDone) {
        const successCount = uploadedFiles.filter(
          (f) => f.status === "success"
        ).length;
        if (successCount > 0) {
          setAlert({
            show: true,
            message: `Successfully uploaded ${successCount} of ${uploadedFiles.length} files`,
            type: "success",
          });

          // Invalidate file attachment queries to refresh the list
          queryClient.invalidateQueries({
            queryKey: bonusKeys.fileAttachments.all,
          });
        }
      }
    },
  });

  const onDrop = useCallback((acceptedFiles: File[]) => {
    // Check file types and sizes before processing
    const validFiles = acceptedFiles.filter((file) => {
      // Check if file is a PDF
      if (file.type !== "application/pdf") {
        setAlert({
          show: true,
          message: `File "${file.name}" is not a PDF`,
          type: "error",
        });
        return false;
      }

      // Check file size
      if (file.size > MAX_FILE_SIZE) {
        setAlert({
          show: true,
          message: `File "${
            file.name
          }" exceeds the 10MB size limit (${formatFileSize(file.size)})`,
          type: "error",
        });
        return false;
      }
      return true;
    });

    // Only process files that passed validation
    const newFiles = validFiles.map((file) => ({
      file,
      preview: URL.createObjectURL(file),
      status: "pending" as const,
      id: `file_${fileCountRef.current++}_${Date.now()}`,
      retryCount: 0,
    }));

    if (newFiles.length > 0) {
      setUploadedFiles((prev) => [...prev, ...newFiles]);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive, fileRejections } =
    useDropzone({
      onDrop,
      accept: {
        "application/pdf": [".pdf"], // Only accept PDF files
      },
      maxSize: MAX_FILE_SIZE, // 10MB
      onDropRejected: (fileRejections) => {
        // Create more user-friendly error messages
        const errors = fileRejections.map((rejection) => {
          const file = rejection.file;
          if (rejection.errors[0].code === "file-too-large") {
            return `"${file.name}" is too large: ${formatFileSize(
              file.size
            )}. Maximum size is 10MB.`;
          }
          if (rejection.errors[0].code === "file-invalid-type") {
            return `"${file.name}" has an unsupported file type. Only PDF files are allowed.`;
          }
          return `"${file.name}": ${rejection.errors[0].message}`;
        });

        setAlert({
          show: true,
          message:
            errors.length === 1
              ? errors[0]
              : `${errors.length} files could not be added: ${errors[0]} ${
                  errors.length > 1 ? "(and others)" : ""
                }`,
          type: "error",
        });
      },
    });

  const removeFile = (index: number) => {
    setUploadedFiles((prev) => {
      const newFiles = [...prev];
      // Revoke the preview URL to avoid memory leaks
      URL.revokeObjectURL(newFiles[index].preview);
      newFiles.splice(index, 1);
      return newFiles;
    });
  };

  const retryUpload = async (fileIndex: number) => {
    const fileObj = uploadedFiles[fileIndex];

    // Increment retry count
    setUploadedFiles((prev) => {
      const updated = [...prev];
      updated[fileIndex] = {
        ...updated[fileIndex],
        status: "retrying",
        retryCount: (fileObj.retryCount || 0) + 1,
      };
      return updated;
    });

    try {
      await uploadMutation.mutateAsync({
        file: fileObj.file,
        roleType: selectedRole,
        quarter: isOpsRole ? currentQuarter : null,
      });

      // Update file status to success
      setUploadedFiles((prev) => {
        const updated = [...prev];
        updated[fileIndex] = {
          ...updated[fileIndex],
          status: "success",
          message: "File uploaded successfully",
        };
        return updated;
      });
    } catch (error: any) {
      console.error("Retry upload error:", error);

      // Update file status to error with retry message
      setUploadedFiles((prev) => {
        const updated = [...prev];
        updated[fileIndex] = {
          ...updated[fileIndex],
          status: "error",
          message: `Upload failed${
            (fileObj.retryCount || 0) >= 3 ? ". Max retries reached" : ""
          }`,
        };
        return updated;
      });
    }
  };

  const uploadFiles = async () => {
    const filesToUpload = uploadedFiles.filter((f) => f.status === "pending");

    if (filesToUpload.length === 0) {
      return;
    }

    setAlert({
      show: true,
      message: `Uploading ${filesToUpload.length} file(s)`,
      type: "info",
    });

    for (let i = 0; i < filesToUpload.length; i++) {
      const fileObj = filesToUpload[i];
      const index = uploadedFiles.findIndex((f) => f.id === fileObj.id);

      setUploadedFiles((prev) => {
        const updated = [...prev];
        updated[index] = { ...updated[index], status: "uploading" };
        return updated;
      });

      try {
        await uploadMutation.mutateAsync({
          file: fileObj.file,
          roleType: selectedRole,
          quarter: isOpsRole ? currentQuarter : null,
          retryCount: 0,
        });

        // Update file status to success
        setUploadedFiles((prev) => {
          const updated = [...prev];
          updated[index] = {
            ...updated[index],
            status: "success",
            message: "File uploaded successfully",
          };
          return updated;
        });
      } catch (error: any) {
        console.error("Upload error:", error);

        // Update file status to error
        setUploadedFiles((prev) => {
          const updated = [...prev];
          updated[index] = {
            ...updated[index],
            status: "error",
            message:
              error.response?.data?.error || "Upload failed. Click to retry",
          };
          return updated;
        });
      }
    }

    // If all uploads are done, invalidate file attachment queries to refresh the list
    const allDone = uploadedFiles.every(
      (f) => f.status === "success" || f.status === "error"
    );
    if (allDone) {
      queryClient.invalidateQueries({
        queryKey: bonusKeys.fileAttachments.all,
      });
    }
  };

  const retryAllFailedUploads = () => {
    uploadedFiles.forEach((file, index) => {
      if (file.status === "error" && (file.retryCount || 0) < 3) {
        retryUpload(index);
      }
    });
  };

  const clearCompletedUploads = () => {
    setUploadedFiles((prev) =>
      prev.filter(
        (file) =>
          file.status === "pending" ||
          file.status === "uploading" ||
          file.status === "retrying"
      )
    );
  };

  const breadcrumbPages = [
    {
      name: "Document Upload",
      href: "/upload",
      current: true,
      icon: (props: React.ComponentProps<"svg">) => (
        <ArrowUpTrayIcon
          {...props}
          className="w-6 h-6 text-primary-500 transition duration-75 group-hover:text-gray-900"
        />
      ),
    },
  ];

  // Clean up URLs when component unmounts
  React.useEffect(() => {
    return () => {
      uploadedFiles.forEach((file) => {
        URL.revokeObjectURL(file.preview);
      });
    };
  }, [uploadedFiles]);

  const getFileIcon = (fileName: string) => {
    return <DocumentTextIcon className="w-12 h-12 text-red-600" />;
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "uploading":
      case "retrying":
        return <CircularProgress size={24} />;
      case "success":
        return <CheckCircleIcon className="w-6 h-6 text-green-500" />;
      case "error":
        return <ExclamationCircleIcon className="w-6 h-6 text-red-500" />;
      default:
        return null;
    }
  };

  const handleCloseAlert = () => {
    setAlert((prev) => ({ ...prev, show: false }));
  };

  // Helper function to format date from ISO string to readable format
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Helper function to format file size
  const formatFileSizeFromBytes = (bytes: number): string => {
    if (bytes < 1024) return bytes + " B";
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
    return (bytes / (1024 * 1024)).toFixed(1) + " MB";
  };

  // Function to download a file
  const handleDownloadFile = async (fileId: number, fileName: string) => {
    try {
      const blob = await downloadFileMutation.mutateAsync(fileId);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error("Error downloading file:", error);
      setAlert({
        show: true,
        message: "Failed to download file. Please try again.",
        type: "error",
      });
    }
  };

  // Function to delete a file
  const handleDeleteFile = async (fileId: number) => {
    if (window.confirm("Are you sure you want to delete this file?")) {
      try {
        await deleteFileMutation.mutateAsync(fileId);
        setAlert({
          show: true,
          message: "File deleted successfully.",
          type: "success",
        });
      } catch (error) {
        console.error("Error deleting file:", error);
        setAlert({
          show: true,
          message: "Failed to delete file. Please try again.",
          type: "error",
        });
      }
    }
  };

  // Stats for summary
  const pendingCount = uploadedFiles.filter(
    (f) => f.status === "pending"
  ).length;
  const successCount = uploadedFiles.filter(
    (f) => f.status === "success"
  ).length;
  const errorCount = uploadedFiles.filter((f) => f.status === "error").length;
  const processingCount = uploadedFiles.filter(
    (f) => f.status === "uploading" || f.status === "retrying"
  ).length;

  // Handle downloading multiple selected files
  const handleDownloadSelectedFiles = async () => {
    if (selectedFiles.size === 0) return;

    // Get the selected file objects from the fileAttachmentsQuery data
    const filesToDownload =
      fileAttachmentsQuery.data?.filter((file) => selectedFiles.has(file.id)) ||
      [];

    if (filesToDownload.length === 0) return;

    // Show loading message
    setAlert({
      show: true,
      message: `Preparing ${filesToDownload.length} files for download...`,
      type: "info",
    });

    try {
      // For each file, download it but don't prompt for each one
      const downloads = filesToDownload.map(async (file) => {
        try {
          const blob = await downloadFileMutation.mutateAsync(file.id);
          const url = window.URL.createObjectURL(blob);

          // Create a link element and click it programmatically
          const a = document.createElement("a");
          a.href = url;
          a.download = file.fileName;
          a.style.display = "none";
          document.body.appendChild(a);
          a.click();

          // Clean up
          setTimeout(() => {
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
          }, 100);

          return { success: true, fileName: file.fileName };
        } catch (error) {
          console.error(`Failed to download ${file.fileName}:`, error);
          return { success: false, fileName: file.fileName };
        }
      });

      // Wait for all downloads to complete
      const results = await Promise.all(downloads);

      // Show completion message
      const successCount = results.filter((r) => r.success).length;
      const failureCount = results.length - successCount;

      setAlert({
        show: true,
        message: `Downloaded ${successCount} files${
          failureCount > 0 ? `, ${failureCount} failed` : ""
        }`,
        type: failureCount > 0 ? "error" : "success",
      });
    } catch (error) {
      console.error("Error downloading files:", error);
      setAlert({
        show: true,
        message: "Failed to download selected files. Please try again.",
        type: "error",
      });
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <div className="xl:pl-0 flex flex-col">
        <main className="flex-grow flex flex-col overflow-hidden">
          <div className="relative isolate overflow-hidden pt-6 flex-grow flex flex-col">
            <div className="mx-auto w-full px-4 sm:px-6 lg:px-8">
              {/* Breadcrumb */}
              <div className="flex items-center justify-between mb-6">
                <Breadcrumb pages={breadcrumbPages} />
              </div>

              {/* Upload Section */}
              <div className="bg-white rounded-lg shadow-md p-6 mb-6">
                <h2 className="text-xl font-semibold mb-4 text-gray-800 flex items-center">
                  <PaperClipIcon className="w-6 h-6 mr-2 text-primary-500" />
                  Upload Documents
                </h2>

                {/* Dropzone */}
                <div
                  {...getRootProps()}
                  className={`border-2 border-dashed rounded-lg p-12 text-center cursor-pointer transition-all ${
                    isDragActive
                      ? "border-primary-500 bg-primary-50"
                      : "border-gray-300 hover:border-primary-400 hover:bg-gray-50"
                  }`}
                >
                  <input {...getInputProps()} />
                  <ArrowUpTrayIcon className="mx-auto h-12 w-12 text-gray-400" />
                  <p className="mt-4 text-sm font-medium text-gray-900">
                    {isDragActive
                      ? "Drop the PDF files here"
                      : "Drag and drop PDF files here, or click to select files"}
                  </p>
                  <p className="mt-2 text-xs text-gray-500">
                    Only PDF files up to 10MB are supported
                  </p>
                </div>

                {/* Commented Bonus Application ID field */}
                {/* <div className="mt-4">
                  <label htmlFor="bonusApplicationId" className="block text-sm font-medium text-gray-700 mb-1">
                    Bonus Application ID (optional)
                  </label>
                  <input
                    type="text"
                    id="bonusApplicationId"
                    placeholder="Enter Bonus Application ID if applicable"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                    value={bonusApplicationId}
                    onChange={(e) => setBonusApplicationId(e.target.value)}
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    If you're uploading a document for a specific bonus application, enter the ID here.
                  </p>
                </div> */}
              </div>

              {/* Preview & Upload Section */}
              {uploadedFiles.length > 0 && (
                <div className="bg-white rounded-lg shadow-md p-6">
                  <div className="flex justify-between items-center mb-6">
                    <h3 className="text-lg font-semibold text-gray-800 flex items-center">
                      <DocumentIcon className="w-5 h-5 mr-2 text-primary-500" />
                      Selected Files
                      <span className="ml-2 text-sm font-normal text-gray-500">
                        ({uploadedFiles.length} file
                        {uploadedFiles.length !== 1 ? "s" : ""})
                      </span>
                    </h3>

                    {/* Action buttons */}
                    <div className="flex space-x-3">
                      {errorCount > 0 && (
                        <button
                          onClick={retryAllFailedUploads}
                          className="px-3 py-1.5 text-sm bg-blue-50 text-blue-700 rounded border border-blue-200 hover:bg-blue-100 transition-colors flex items-center"
                        >
                          <ArrowPathIcon className="w-4 h-4 mr-1" />
                          Retry Failed
                        </button>
                      )}

                      {successCount > 0 && (
                        <button
                          onClick={clearCompletedUploads}
                          className="px-3 py-1.5 text-sm bg-gray-50 text-gray-700 rounded border border-gray-200 hover:bg-gray-100 transition-colors"
                        >
                          Clear Completed
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Status summary */}
                  {uploadedFiles.length > 0 && (
                    <div className="flex space-x-4 mb-4">
                      <div className="flex items-center">
                        <div className="w-3 h-3 bg-gray-300 rounded-full mr-2"></div>
                        <span className="text-sm text-gray-600">
                          Pending: {pendingCount}
                        </span>
                      </div>
                      <div className="flex items-center">
                        <div className="w-3 h-3 bg-blue-400 rounded-full mr-2"></div>
                        <span className="text-sm text-gray-600">
                          Processing: {processingCount}
                        </span>
                      </div>
                      <div className="flex items-center">
                        <div className="w-3 h-3 bg-green-500 rounded-full mr-2"></div>
                        <span className="text-sm text-gray-600">
                          Success: {successCount}
                        </span>
                      </div>
                      <div className="flex items-center">
                        <div className="w-3 h-3 bg-red-500 rounded-full mr-2"></div>
                        <span className="text-sm text-gray-600">
                          Failed: {errorCount}
                        </span>
                      </div>
                    </div>
                  )}

                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5 mb-8">
                    {uploadedFiles.map((fileObj, index) => {
                      const isImage = /\.(jpe?g|png|gif)$/i.test(
                        fileObj.file.name
                      );
                      const fileIcon = getFileIcon(fileObj.file.name);
                      const statusIcon = getStatusIcon(fileObj.status);
                      const isRetryable =
                        fileObj.status === "error" &&
                        (fileObj.retryCount || 0) < 3;

                      return (
                        <div
                          key={fileObj.id || index}
                          className={`relative border rounded-lg overflow-hidden shadow-sm transition-all hover:shadow-md ${
                            fileObj.status === "error"
                              ? "border-red-200 bg-red-50"
                              : fileObj.status === "success"
                              ? "border-green-200 bg-green-50"
                              : fileObj.status === "uploading" ||
                                fileObj.status === "retrying"
                              ? "border-blue-200 bg-blue-50"
                              : "border-gray-200 hover:border-primary-200"
                          }`}
                        >
                          {/* Preview or Icon */}
                          <div
                            className={`h-40 flex items-center justify-center overflow-hidden bg-gray-100 ${
                              isRetryable
                                ? "cursor-pointer"
                                : fileObj.status !== "uploading" &&
                                  fileObj.status !== "retrying"
                                ? "cursor-pointer"
                                : ""
                            }`}
                            onClick={() => {
                              if (isRetryable) {
                                retryUpload(index);
                              } else if (
                                fileObj.status !== "uploading" &&
                                fileObj.status !== "retrying"
                              ) {
                                setPreviewFile(fileObj);
                              }
                            }}
                          >
                            {isRetryable ? (
                              <div className="text-center p-4 hover:scale-110 transition-transform">
                                <ArrowPathIcon className="w-12 h-12 text-red-500 mx-auto" />
                                <p className="mt-2 text-sm text-red-600 font-medium">
                                  Click to retry
                                </p>
                              </div>
                            ) : isImage ? (
                              <img
                                src={fileObj.preview}
                                alt={fileObj.file.name}
                                className="max-h-full max-w-full object-contain hover:scale-105 transition-transform"
                              />
                            ) : (
                              <div className="text-center p-4 hover:scale-110 transition-transform">
                                {fileIcon}
                                <p className="mt-2 text-xs text-gray-500 truncate max-w-[140px]">
                                  {fileObj.status === "uploading" ||
                                  fileObj.status === "retrying"
                                    ? "Uploading..."
                                    : "Click to preview"}
                                </p>
                              </div>
                            )}
                          </div>

                          {/* File Name & Status */}
                          <div className="p-3 bg-white">
                            <div className="flex items-start justify-between">
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-gray-900 truncate">
                                  {fileObj.file.name}
                                </p>
                                <p className="text-xs text-gray-500">
                                  {(fileObj.file.size / 1024).toFixed(0)} KB
                                </p>
                                {fileObj.message && (
                                  <p
                                    className={`text-xs mt-1 ${
                                      fileObj.status === "error"
                                        ? "text-red-500"
                                        : fileObj.status === "uploading" ||
                                          fileObj.status === "retrying"
                                        ? "text-blue-500"
                                        : "text-green-500"
                                    }`}
                                  >
                                    {fileObj.status === "retrying"
                                      ? `Retrying (${fileObj.retryCount}/3)...`
                                      : fileObj.message}
                                  </p>
                                )}
                              </div>
                              <div className="flex items-center ml-2">
                                {statusIcon}
                                {fileObj.status === "pending" && (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      removeFile(index);
                                    }}
                                    className="ml-2 text-gray-400 hover:text-red-500"
                                  >
                                    <XCircleIcon className="h-5 w-5" />
                                  </button>
                                )}
                              </div>
                            </div>
                          </div>

                          {/* Status Indicator */}
                          {fileObj.status !== "pending" && (
                            <div
                              className={`absolute top-0 right-0 m-2 p-1 rounded-full ${
                                fileObj.status === "success"
                                  ? "bg-green-100"
                                  : fileObj.status === "error"
                                  ? "bg-red-100"
                                  : "bg-blue-100"
                              }`}
                            >
                              {statusIcon}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  {/* Upload Button */}
                  <div className="flex justify-end">
                    <button
                      onClick={uploadFiles}
                      disabled={
                        uploadMutation.isPending ||
                        !uploadedFiles.some((f) => f.status === "pending")
                      }
                      className="px-6 py-2.5 bg-primary-600 text-white rounded-md shadow-sm hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50 transition-colors font-medium flex items-center"
                    >
                      {uploadMutation.isPending ? (
                        <>
                          <CircularProgress
                            size={20}
                            color="inherit"
                            className="mr-2"
                          />
                          Uploading...
                        </>
                      ) : (
                        <>
                          <ArrowUpTrayIcon className="w-5 h-5 mr-2" />
                          Upload Files
                        </>
                      )}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </main>
      </div>

      {/* New Existing Documents Section */}
      <div className="mx-auto w-full px-4 sm:px-6 lg:px-8 mb-10">
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold mb-4 text-gray-800 flex items-center">
            <DocumentChartBarIcon className="w-6 h-6 mr-2 text-primary-500" />
            Existing Documents
          </h2>

          {/* View selector */}
          <div className="mb-6">
            <label
              htmlFor="view-type"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              View Documents By
            </label>
            <div className="flex space-x-4">
              {/* Only show Quarter option for Ops users */}
              {isOpsRole && (
                <button
                  onClick={() => setViewType("quarter")}
                  className={`px-4 py-2 text-sm rounded-md ${
                    viewType === "quarter"
                      ? "bg-primary-600 text-white"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  }`}
                >
                  Current Quarter (2025 Q1)
                </button>
              )}
              {/* <button
                onClick={() => setViewType("employee")}
                className={`px-4 py-2 text-sm rounded-md ${
                  viewType === "employee"
                    ? "bg-primary-600 text-white"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
              >
                My Uploaded Documents
              </button> */}
              <button
                onClick={() => setViewType("email")}
                className={`px-4 py-2 text-sm rounded-md ${
                  viewType === "email"
                    ? "bg-primary-600 text-white"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
              >
                My Uploaded Documents
              </button>
            </div>
          </div>

          {/* Search box */}
          <div className="mb-6">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                className="block w-full pl-10 pr-10 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                placeholder="Search files by name..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setCurrentPage(1); // Reset to first page on new search
                }}
              />
              {searchQuery && (
                <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                  <button
                    onClick={() => {
                      setSearchQuery("");
                      setCurrentPage(1);
                    }}
                    className="text-gray-400 hover:text-gray-500 focus:outline-none"
                  >
                    <XCircleIcon className="h-5 w-5" />
                  </button>
                </div>
              )}
            </div>
            {searchQuery && filteredAttachments.length > 0 && (
              <div className="mt-2 text-sm text-gray-500">
                Found {filteredAttachments.length} results for "{searchQuery}"
              </div>
            )}
          </div>

          {/* Multi-select actions */}
          {fileAttachmentsQuery.data &&
            fileAttachmentsQuery.data.length > 0 && (
              <div className="flex justify-between items-center mb-4">
                <div className="flex items-center">
                  <button
                    onClick={() => {
                      setIsSelectMode(!isSelectMode);
                      if (isSelectMode) {
                        // Clear selections when exiting select mode
                        setSelectedFiles(new Set());
                      }
                    }}
                    className={`px-3 py-1.5 text-sm rounded border transition-colors flex items-center ${
                      isSelectMode
                        ? "bg-primary-100 text-primary-700 border-primary-300"
                        : "bg-gray-50 text-gray-700 border-gray-200 hover:bg-gray-100"
                    }`}
                  >
                    {isSelectMode ? "Cancel Selection" : "Select Files"}
                  </button>

                  {isSelectMode && (
                    <>
                      <span className="mx-2 text-sm text-gray-500">
                        {selectedFiles.size} selected
                      </span>
                      <span className="mx-2 text-xs text-gray-400 italic">
                        (Click on rows to select files)
                      </span>
                      <button
                        onClick={() => {
                          // Select all files on current page
                          const newSelectedFiles = new Set(selectedFiles);
                          paginatedAttachments.forEach((file) => {
                            newSelectedFiles.add(file.id);
                          });
                          setSelectedFiles(newSelectedFiles);
                        }}
                        className="ml-2 px-3 py-1.5 text-sm bg-gray-50 text-gray-700 rounded border border-gray-200 hover:bg-gray-100 transition-colors"
                      >
                        Select All on Page
                      </button>
                      <button
                        onClick={() => setSelectedFiles(new Set())}
                        className="ml-2 px-3 py-1.5 text-sm bg-gray-50 text-gray-700 rounded border border-gray-200 hover:bg-gray-100 transition-colors"
                      >
                        Clear Selection
                      </button>
                    </>
                  )}
                </div>

                {/* Download selected button */}
                {isSelectMode && selectedFiles.size > 0 && (
                  <button
                    onClick={() => handleDownloadSelectedFiles()}
                    disabled={downloadFileMutation.isPending}
                    className="px-3 py-1.5 text-sm bg-primary-600 text-white rounded hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50 transition-colors flex items-center"
                  >
                    {downloadFileMutation.isPending ? (
                      <CircularProgress size={16} className="mr-1" />
                    ) : (
                      <CloudArrowDownIcon className="h-4 w-4 mr-1" />
                    )}
                    Download Selected ({selectedFiles.size})
                  </button>
                )}
              </div>
            )}

          {fileAttachmentsQuery.isLoading ? (
            <div className="flex justify-center items-center py-10">
              <CircularProgress />
              <span className="ml-3 text-gray-600">Loading documents ...</span>
            </div>
          ) : fileAttachmentsQuery.isError ? (
            <div className="text-red-500 p-4 bg-red-50 rounded-md">
              Error loading documents. Please try again later.
            </div>
          ) : fileAttachmentsQuery.data &&
            fileAttachmentsQuery.data.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    {/* Checkbox column for selections */}
                    {isSelectMode && (
                      <th className="px-6 py-3 w-10">
                        <div className="flex items-center justify-center">
                          <input
                            type="checkbox"
                            className="h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                            checked={
                              paginatedAttachments.length > 0 &&
                              paginatedAttachments.every((file) =>
                                selectedFiles.has(file.id)
                              )
                            }
                            onChange={(e) => {
                              if (e.target.checked) {
                                // Select all files on current page
                                const newSelectedFiles = new Set(selectedFiles);
                                paginatedAttachments.forEach((file) => {
                                  newSelectedFiles.add(file.id);
                                });
                                setSelectedFiles(newSelectedFiles);
                              } else {
                                // Unselect all files on current page
                                const newSelectedFiles = new Set(selectedFiles);
                                paginatedAttachments.forEach((file) => {
                                  newSelectedFiles.delete(file.id);
                                });
                                setSelectedFiles(newSelectedFiles);
                              }
                            }}
                          />
                        </div>
                      </th>
                    )}
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      File Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Quarter
                    </th>
                    {/* <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Size
                    </th> */}
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Upload Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Uploader
                    </th>                   
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {paginatedAttachments.map((file) => (
                    <tr
                      key={file.id}
                      className={`hover:bg-gray-50 ${
                        isSelectMode && selectedFiles.has(file.id)
                          ? "bg-primary-50"
                          : ""
                      } cursor-pointer`}
                      onClick={() => {
                        if (isSelectMode) {
                          // Toggle selection when in select mode
                          const newSelectedFiles = new Set(selectedFiles);
                          if (selectedFiles.has(file.id)) {
                            newSelectedFiles.delete(file.id);
                          } else {
                            newSelectedFiles.add(file.id);
                          }
                          setSelectedFiles(newSelectedFiles);
                        }
                      }}
                    >
                      {/* Checkbox for selection */}
                      {isSelectMode && (
                        <td className="px-6 py-4 whitespace-nowrap w-10">
                          <div className="flex items-center justify-center">
                            <input
                              type="checkbox"
                              className="h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                              checked={selectedFiles.has(file.id)}
                              onChange={(e) => {
                                e.stopPropagation(); // Prevent row click from toggling again
                                const newSelectedFiles = new Set(selectedFiles);
                                if (e.target.checked) {
                                  newSelectedFiles.add(file.id);
                                } else {
                                  newSelectedFiles.delete(file.id);
                                }
                                setSelectedFiles(newSelectedFiles);
                              }}
                            />
                          </div>
                        </td>
                      )}
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <DocumentTextIcon className="h-5 w-5 text-red-500 mr-2" />
                          <span className="text-sm font-medium text-gray-900 truncate max-w-xs">
                            {file.fileName}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {file.quarter || "N/A"}
                      </td>                      
                      {/* <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatFileSizeFromBytes(file.fileSize)}
                      </td> */}
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {file?.createdOn ? formatDate(file.createdOn) : ""}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {file?.createdBy ??  ""}
                      </td>

                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <div className="flex space-x-3">
                          <button
                            onClick={(e) => {
                              e.stopPropagation(); // Prevent row click when clicking download
                              handleDownloadFile(file.id, file.fileName);
                            }}
                            className="text-primary-500 hover:text-primary-600 flex items-center"
                            disabled={downloadFileMutation.isPending}
                          >
                            {downloadFileMutation.isPending ? (
                              <CircularProgress size={16} className="mr-1" />
                            ) : (
                              <CloudArrowDownIcon className="h-4 w-4 mr-1" />
                            )}
                            Download
                          </button>
                          {/* <button
                            onClick={() => handleDeleteFile(file.id)}
                            className="text-red-600 hover:text-red-900 flex items-center"
                            disabled={deleteFileMutation.isPending}
                          >
                            {deleteFileMutation.isPending ? (
                              <CircularProgress size={16} className="mr-1" />
                            ) : (
                              <TrashIcon className="h-4 w-4 mr-1" />
                            )}
                            Delete
                          </button> */}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-10 text-gray-500">
              {searchQuery
                ? `No results found for "${searchQuery}". Try different search terms.`
                : viewType === "quarter"
                ? `No documents found for ${currentQuarter}.`
                :  `No documents found for the employee`}
            </div>
          )}

          {/* Pagination */}
          {filteredAttachments.length > 0 && (
            <div className="flex flex-col sm:flex-row justify-center items-center mt-6 gap-4">
              <Pagination
                count={totalPages}
                page={currentPage}
                onChange={handlePageChange}
                color="primary"
                showFirstButton
                showLastButton
                siblingCount={1}
                size="medium"
              />
            </div>
          )}
        </div>
      </div>

      {/* File Preview Modal */}
      {previewFile && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] flex flex-col">
            <div className="flex justify-between items-center p-4 border-b">
              <h3 className="font-medium text-gray-900 truncate">
                {previewFile.file.name}
              </h3>
              <button
                onClick={() => setPreviewFile(null)}
                className="text-gray-500 hover:text-gray-700"
              >
                <XCircleIcon className="w-6 h-6" />
              </button>
            </div>

            <div className="flex-1 overflow-auto p-4 flex items-center justify-center bg-gray-100">
              <iframe
                src={previewFile.preview}
                title={previewFile.file.name}
                className="w-full h-[70vh]"
              />
            </div>

            <div className="p-4 border-t flex justify-end">
              <button
                onClick={() => setPreviewFile(null)}
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 mr-2"
              >
                Close
              </button>
              <a
                href={previewFile.preview}
                download={previewFile.file.name}
                className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700"
              >
                Download
              </a>
            </div>
          </div>
        </div>
      )}

      {/* Notification system */}
      <Snackbar
        open={alert.show}
        autoHideDuration={6000}
        onClose={handleCloseAlert}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseAlert}
          severity={alert.type}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {alert.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default Upload;
